import { useEffect, useState } from 'react';
import { pickCentre, visibleFraction } from '../utils/centrePick';

/*
 * One-at-a-time muted autoplay (VIDEO_PLAN §12, Kurtis's phone test):
 *  - the element that plays is the one nearest the centre of the screen,
 *    among those at least half visible, chosen once scrolling settles
 *    (120 ms after the last scroll event);
 *  - one plays at a time, per scroll root;
 *  - one scrolled mostly out of view stops AT ONCE, not when scrolling settles
 *    (its caller unmounts the <video>, which drops the download).
 *
 * Every caller sharing a root shares one coordinator, so a video cover in the
 * trip list and one in a recap row can't both play. A full-screen viewer
 * passes its own scroll container as `root` and gets its own.
 *
 * `respectPreferences` (default true): nothing autoplays under
 * prefers-reduced-motion or Save-Data. A viewer the person opened to watch
 * passes false — playing the clip they tapped is not autoplay.
 *
 * useCentreAutoplay(ref, { root, enabled, respectPreferences }): `ref` is the
 * media element's box; `root` a scroll container (element or ref), null for
 * the window. Returns whether this element should be playing.
 */
export const SETTLE_MS = 120;

const coordinators = new Map();

function windowViewport() {
  return { top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth };
}

function coordinatorFor(root) {
  const existing = coordinators.get(root);
  if (existing) return existing;

  const entries = new Map(); // key → { el, setActive }
  let activeKey = null;
  let timer = null;
  let nextKey = 1;

  const viewport = () => {
    const view = windowViewport();
    if (!root) return view;
    const r = root.getBoundingClientRect();
    return {
      top: Math.max(r.top, view.top), bottom: Math.min(r.bottom, view.bottom),
      left: Math.max(r.left, view.left), right: Math.min(r.right, view.right)
    };
  };
  const setActiveKey = (key) => {
    if (key === activeKey) return;
    entries.get(activeKey)?.setActive(false);
    activeKey = key;
    entries.get(activeKey)?.setActive(true);
  };
  const settle = () => {
    timer = null;
    const view = viewport();
    const candidates = [];
    for (const [key, { el }] of entries) {
      if (el.isConnected) candidates.push({ id: key, rect: el.getBoundingClientRect() });
    }
    setActiveKey(pickCentre(candidates, view));
  };
  const schedule = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(settle, SETTLE_MS);
  };
  // While scrolling: the playing one stops the moment it is mostly gone.
  const onScroll = () => {
    const playing = entries.get(activeKey);
    if (playing && visibleFraction(playing.el.getBoundingClientRect(), viewport()) < 0.5) setActiveKey(null);
    schedule();
  };
  const onScrollEnd = () => {
    if (timer) clearTimeout(timer);
    settle();
  };

  const listen = () => {
    // Capture: a scroll inside any container moves what's on screen, and a
    // scroll event doesn't bubble.
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    document.addEventListener('scrollend', onScrollEnd, { capture: true, passive: true });
    window.addEventListener('resize', schedule, { passive: true });
  };
  const unlisten = () => {
    document.removeEventListener('scroll', onScroll, { capture: true });
    document.removeEventListener('scrollend', onScrollEnd, { capture: true });
    window.removeEventListener('resize', schedule);
    if (timer) clearTimeout(timer);
    timer = null;
  };

  const coordinator = {
    add(el, setActive) {
      if (entries.size === 0) listen();
      const key = nextKey++;
      entries.set(key, { el, setActive });
      schedule();
      return key;
    },
    remove(key) {
      if (key === activeKey) activeKey = null;
      entries.delete(key);
      if (entries.size === 0) {
        unlisten();
        coordinators.delete(root);
      } else {
        schedule();
      }
    }
  };
  coordinators.set(root, coordinator);
  return coordinator;
}

/** Whether the person's settings allow video to start by itself. */
export function autoplayAllowed() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
  } catch { /* old browsers: no preference */ }
  if (typeof navigator !== 'undefined' && navigator.connection?.saveData) return false;
  return true;
}

export default function useCentreAutoplay(ref, { root = null, enabled = true, respectPreferences = true } = {}) {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    // Read here, not in render: on the first render a container's ref is
    // still null, and the window's coordinator would be used instead.
    const rootEl = root && typeof root === 'object' && 'current' in root ? root.current : root;
    if (!enabled || !el || (respectPreferences && !autoplayAllowed())) {
      setActive(false);
      return undefined;
    }
    const coordinator = coordinatorFor(rootEl || null);
    const key = coordinator.add(el, setActive);
    return () => {
      coordinator.remove(key);
      setActive(false);
    };
  }, [ref, root, enabled, respectPreferences]);

  return active;
}
